import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Search } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import EditParcelaModal from '../../components/Admin/EditParcelaModal';
import { Moeda, formatMoeda } from '../../utils/orcamentoPagamento';
import { ActionButton, Button, Pagination } from '../../components/UI';

interface PagamentoItem {
  _id: string;
  numeroParcela: number;
  totalParcelas: number;
  valor: number;
  moeda?: Moeda;
  vencimento: string;
  status: 'pendente' | 'pago' | 'atrasado' | 'cancelado';
  clienteId: { _id: string; nome: string };
  vendaId: { _id: string; descricao: string };
}

const STATUS_VALUES = ['pendente', 'pago', 'atrasado', 'cancelado'];

const STATUS_LABELS: { [key: string]: string } = {
  pendente: 'Pendente',
  pago: 'Pago',
  atrasado: 'Atrasado',
  cancelado: 'Cancelado'
};

const STATUS_COLORS: { [key: string]: string } = {
  pendente: 'bg-yellow-100 text-yellow-800',
  pago: 'bg-green-100 text-green-800',
  atrasado: 'bg-red-100 text-red-800',
  cancelado: 'bg-gray-100 text-gray-800'
};

interface PagamentosProps {
  embedded?: boolean;
}

const Pagamentos: React.FC<PagamentosProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [pagamentos, setPagamentos] = useState<PagamentoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingPagamento, setEditingPagamento] = useState<PagamentoItem | null>(null);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchPagamentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(selectedStatus && { status: selectedStatus }),
      });

      const response = await axios.get(`/pagamentos?${params}`);
      setPagamentos(response.data.pagamentos);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, currentPage]);

  useEffect(() => {
    fetchPagamentos();
  }, [fetchPagamentos]);

  const handleMarcarPago = async (pagamentoId: string) => {
    try {
      const response = await axios.put(`/pagamentos/${pagamentoId}/pagar`);
      setPagamentos((prev) => prev.map((p) => (p._id === pagamentoId ? response.data : p)));
      notifications.success.pagamentoRegistrado();
    } catch (error: any) {
      console.error('Erro ao marcar pagamento como pago:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao marcar pagamento como pago');
    }
  };

  const handleExcluirPagamento = async (pagamentoId: string) => {
    if (!(await confirm({ title: 'Excluir parcela', message: 'Esta parcela será removida permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/pagamentos/${pagamentoId}`);
      setPagamentos((prev) => prev.filter((p) => p._id !== pagamentoId));
      notifications.success.pagamentoExcluido();
    } catch (error: any) {
      console.error('Erro ao excluir pagamento:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao excluir parcela');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (pagamento: (typeof pagamentos)[number]) => (
    <div className="row-actions md:justify-end">
      {(pagamento.status === 'pendente' || pagamento.status === 'atrasado') && (
        <ActionButton
          action="approve"
          label="Marcar pago"
          onClick={() => handleMarcarPago(pagamento._id)}
        />
      )}
      <ActionButton
        action="edit"
        onClick={() => setEditingPagamento(pagamento)}
      />
      {pagamento.status !== 'pago' && (
        <ActionButton
          action="delete"
          onClick={() => handleExcluirPagamento(pagamento._id)}
        />
      )}
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {!embedded && (
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.pagamentos')}</h1>
              <p className="text-gray-600">{t('admin.pagamentosDesc')}</p>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8'}>
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedStatus('')}
              className={`toggle-chip px-3 py-1.5 rounded-lg text-sm font-medium ${selectedStatus === '' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Todos
            </button>
            {STATUS_VALUES.map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`toggle-chip px-3 py-1.5 rounded-lg text-sm font-medium ${selectedStatus === status ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {STATUS_LABELS[status]}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : pagamentos.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum pagamento encontrado</h3>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venda</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parcela</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vencimento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagamentos.map((pagamento) => (
                    <tr key={pagamento._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {pagamento.clienteId ? (
                          <Link to={`/admin/clientes/${pagamento.clienteId._id}`} className="text-sm text-primary-600 hover:text-primary-800">
                            {pagamento.clienteId.nome}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{pagamento.vendaId?.descricao || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pagamento.numeroParcela}/{pagamento.totalParcelas}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatMoeda(pagamento.valor, pagamento.moeda)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(pagamento.vencimento)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[pagamento.status]}`}>
                          {STATUS_LABELS[pagamento.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(pagamento)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {pagamentos.map((pagamento) => (
                <li key={pagamento._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    {pagamento.clienteId ? (
                      <Link to={`/admin/clientes/${pagamento.clienteId._id}`} className="text-sm text-primary-600 hover:text-primary-800">
                        {pagamento.clienteId.nome}
                      </Link>
                    ) : '—'}
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Venda</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {pagamento.vendaId?.descricao || '—'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Parcela</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {pagamento.numeroParcela}/{pagamento.totalParcelas}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Valor</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {formatMoeda(pagamento.valor, pagamento.moeda)}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Vencimento</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {formatDate(pagamento.vencimento)}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[pagamento.status]}`}>
                          {STATUS_LABELS[pagamento.status]}
                        </span>
                      </dd>
                    </div>
                  </dl>
                  {renderActions(pagamento)}
                </li>
              ))}
            </ul>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>

      {editingPagamento && (
        <EditParcelaModal
          pagamento={editingPagamento}
          onClose={() => setEditingPagamento(null)}
          onSaved={(updated) =>
            setPagamentos((prev) =>
              prev.map((p) =>
                p._id === updated._id
                  ? { ...p, valor: updated.valor, vencimento: updated.vencimento, status: updated.status }
                  : p
              )
            )
          }
        />
      )}
    </div>
  );
};

export default Pagamentos;
